import styled from "styled-components";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { styled as muiStyled } from "@mui/material/styles";

const StickyDataGrid = muiStyled(DataGridPro, {
  shouldForwardProp: (prop) => prop !== "autoHeight",
})(({ theme, autoHeight }) => ({
  // Basic grid styling - Height is controlled by Container. When autoHeight, no min so grid fits rows.
  height: "100%",
  minHeight: autoHeight ? 0 : "400px",
  width: "100%",

  "& .MuiDataGrid-root": {
    border: "1px solid #e9ecef",
    borderRadius: 0,
    background: "white",
    fontFamily: "Source Sans Pro",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },

  "& .MuiDataGrid-overlay": {
    minHeight: autoHeight ? 0 : "400px",
  },

  // Column headers
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#ffffff !important",
    borderBottom: "1px solid var(--stroke-subdued, #DFE5E8)",
    borderTop: "1px solid #dee2e6",
    minHeight: "52px",
    fontFamily: "Source Sans Pro"
  },

  // Footer/Pagination - scrolls with grid content (remove sticky to hide when scrolling)
  "& .MuiDataGrid-footerContainer": {
    zIndex: "1",
    backgroundColor: "#ffffff !important",
    borderTop: "1px solid #e9ecef !important",
    minHeight: "52px",
    marginTop: "auto",
  },

  "& .MuiDataGrid-columnHeader": {
    color: "#56656B",
    fontFamily: "Source Sans Pro",
    fontWeight: 400,
    fontSize: "14px",
    padding: "0 12px",
    textTransform: "uppercase",
    letterSpacing: "0.025em",
    height: "auto !important",
    minHeight: "52px !important",
    backgroundColor: "#ffffff !important",
  },

  // Checkbox column header - override padding to center checkbox
  "& .MuiDataGrid-columnHeader--checkboxSelection": {
    padding: "0 !important",
    backgroundColor: "#ffffff !important",
  },

  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 400,
    color: "#56656B",
    fontSize: "14px",
    fontFamily: "Source Sans Pro",
    textTransform: "uppercase",
    letterSpacing: "0.025em",
    backgroundColor: "#ffffff !important",
  },

  // Ensure all column header elements have the background color
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    backgroundColor: "#ffffff !important",
  },

  "& .MuiDataGrid-columnHeaderTitleContainerContent": {
    backgroundColor: "#ffffff !important",
  },

  // Comprehensive rule for all column header elements (excluding checkbox to make it visible)
  "& .MuiDataGrid-columnHeaders *:not(.MuiCheckbox-root):not(.MuiSvgIcon-root):not(.MuiButtonBase-root):not(.MuiTouchRipple-root)": {
    backgroundColor: "#ffffff !important",
  },

  // Critical: Ensure checkbox elements have transparent background
  "& .MuiDataGrid-columnHeaders .MuiCheckbox-root": {
    backgroundColor: "transparent !important",
    visibility: "visible !important",
    opacity: "1 !important",
    zIndex: "10 !important",
    position: "relative !important",
  },

  "& .MuiDataGrid-columnHeaders .MuiCheckbox-root *": {
    backgroundColor: "transparent !important",
  },

  "& .MuiDataGrid-columnHeaders .MuiSvgIcon-root": {
    backgroundColor: "transparent !important",
    visibility: "visible !important",
  },

  "& .MuiDataGrid-columnHeaders .MuiButtonBase-root": {
    backgroundColor: "transparent !important",
    visibility: "visible !important",
  },

  // Target the __check__ field specifically
  "& [data-field='__check__'].MuiDataGrid-columnHeader": {
    overflow: "visible !important",
    padding: "0 !important",
    backgroundColor: "#ffffff !important",
  },

  "& [data-field='__check__'] .MuiCheckbox-root": {
    visibility: "visible !important",
    opacity: "1 !important",
    display: "inline-flex !important",
    color: "#56656B !important",
  },

  "& [data-field='__check__'] .MuiCheckbox-root.Mui-checked": {
    color: "#09779E !important",
  },

  "& [data-field='__check__'] .MuiCheckbox-root .MuiSvgIcon-root": {
    visibility: "visible !important",
    opacity: "1 !important",
    fill: "currentColor !important",
  },

  // Fix vertical alignment for NON-PINNED columns only
  "& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--pinnedLeft):not(.MuiDataGrid-columnHeader--pinnedRight)": {
    display: "flex",
    alignItems: "center",
  },

  "& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--pinnedLeft):not(.MuiDataGrid-columnHeader--pinnedRight) .MuiDataGrid-columnHeaderTitleContainer": {
    display: "flex",
    alignItems: "center",
    height: "100%",
  },

  "& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--pinnedLeft):not(.MuiDataGrid-columnHeader--pinnedRight) .MuiDataGrid-columnHeaderTitleContainerContent": {
    display: "flex",
    alignItems: "center",
    height: "100%",
  },

  // Cells
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #f1f3f4",
    fontSize: "16px",
    fontFamily: "Source Sans Pro",
    color: "#56656B",
    padding: "12px",
    minHeight: "52px",
  },

  // Rows
  "& .MuiDataGrid-row": {
    transition: "all 0.2s ease",
    minHeight: "52px",
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },
    "&:nth-of-type(even)": {
      backgroundColor: "#fdfdfd",
    },
    "&:nth-of-type(even):hover": {
      backgroundColor: "#f8f9fa",
    },
  },

  "& .MuiDataGrid-virtualScroller": {
    overflowX: "auto",
    overflowY: "auto",
    flex: "1 1 auto",
    minHeight: 0,
  },

  "& .MuiDataGrid-main": {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    overflow: "hidden",
    minHeight: 0,
  },

  "& .MuiDataGrid-virtualScrollerContent": {
    position: "relative !important",
  },

  // PINNED COLUMNS STYLING - Minimal overrides for performance
  // Left pinned column headers
  "& .MuiDataGrid-columnHeader--pinnedLeft": {
    backgroundColor: "#ffffff !important"
  },

  // Left pinned cells  
  "& .MuiDataGrid-cell--pinnedLeft": {
    backgroundColor: "#ffffff !important",
  },

  // Right pinned column headers - Minimal overrides
  "& .MuiDataGrid-columnHeader--pinnedRight": {
    backgroundColor: "#ffffff !important",
  },

  "& [data-field='leaseName'].MuiDataGrid-columnHeader": {
    borderRight: "1px solid #DFE5E8 !important", // TEST: Blue border - different order
  },

  "& [data-field='leaseName'].MuiDataGrid-cell": {
    borderRight: "1px solid #DFE5E8",
  },
  "& [data-field='actions'].MuiDataGrid-columnHeader": {
    borderLeft: "1px solid #DFE5E8 !important", // TEST: Blue border - different order
  },

  "& [data-field='actions'].MuiDataGrid-cell": {
    borderLeft: "1px solid #DFE5E8",
  },
  // Right pinned cells - Minimal overrides
  "& .MuiDataGrid-cell--pinnedRight": {
    backgroundColor: "#ffffff !important",
  },

  // Row hover effects for pinned columns
  "& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft": {
    backgroundColor: "#f8f9fa !important",
  },
  "& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight": {
    backgroundColor: "#f8f9fa !important",
  },

  // Even row styling for pinned columns
  "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell--pinnedLeft": {
    backgroundColor: "#fdfdfd !important",
  },
  "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell--pinnedRight": {
    backgroundColor: "#fdfdfd !important",
  },

  // Even row hover styling for pinned columns
  "& .MuiDataGrid-row:nth-of-type(even):hover .MuiDataGrid-cell--pinnedLeft": {
    backgroundColor: "#f8f9fa !important",
  },
  "& .MuiDataGrid-row:nth-of-type(even):hover .MuiDataGrid-cell--pinnedRight": {
    backgroundColor: "#f8f9fa !important",
  },

  // STICKY ACTIONS COLUMN STYLING
  // Actions column hover effects
  "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='actions']": {
    backgroundColor: "#f8f9fa !important",
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
  },
  "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field='actions']": {
    backgroundColor: "#fdfdfd !important",
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
  },
  "& .MuiDataGrid-row:nth-of-type(even):hover .MuiDataGrid-cell[data-field='actions']": {
    backgroundColor: "#f8f9fa !important",
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
  },

  // Prevent any scroll-based transforms on actions column
  "& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-cell[data-field='actions']": {
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
    left: "auto !important",
  },
  "& .MuiDataGrid-virtualScrollerRenderZone .MuiDataGrid-cell[data-field='actions']": {
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
    left: "auto !important",
  },

  // Force actions column to ignore all transform styles
  "& .MuiDataGrid-row .MuiDataGrid-cell[data-field='actions']": {
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
    left: "auto !important",
    zIndex: "100 !important",
  },

  // Target the filler cells
  "& .MuiDataGrid-filler[data-field='actions']": {
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
    left: "auto !important",
  },

  // Override inline styles completely - most aggressive
  "& [data-field='actions'][style]": {
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
    left: "auto !important",
  },

  // Target via attribute selector
  '& [data-field="actions"]': {
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
    left: "auto !important",
  },

  // Target our custom class - highest priority
  "& .sticky-actions-cell": {
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
    left: "auto !important",
    zIndex: "100 !important",
    backgroundColor: "#ffffff !important",
    borderLeft: "1px solid #DFE5E8 !important",
    boxShadow: "none !important",
    transition: "none !important",
    animation: "none !important",
    willChange: "auto !important",
  },

  "& .MuiDataGrid-row:hover .sticky-actions-cell": {
    backgroundColor: "#f8f9fa !important",
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
    left: "auto !important",
  },

  "& .MuiDataGrid-row:nth-of-type(even) .sticky-actions-cell": {
    backgroundColor: "#fdfdfd !important",
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
    left: "auto !important",
  },

  "& .MuiDataGrid-row:nth-of-type(even):hover .sticky-actions-cell": {
    backgroundColor: "#f8f9fa !important",
    position: "sticky !important",
    right: "0 !important",
    transform: "none !important",
    left: "auto !important",
  },

  // ============================================================================
  // CHECKBOX STYLING - Unified styling for header and row checkboxes
  // ============================================================================
  
  // Checkbox column header container styling
  "& .MuiDataGrid-columnHeader--checkboxSelection": {
    backgroundColor: "#ffffff !important",
    minWidth: "52px !important",
    maxWidth: "52px !important",
    width: "52px !important",
    padding: "0 !important",
    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",
  },

  // Checkbox column header wrapper (pinned left variant)
  "& .MuiDataGrid-columnHeader--checkboxSelection.MuiDataGrid-columnHeader--pinnedLeft": {
    backgroundColor: "#ffffff !important",
  },

  // Force checkbox header inner wrapper to be visible
  "& .MuiDataGrid-columnHeaderCheckbox": {
    backgroundColor: "transparent !important",
    visibility: "visible !important",
    opacity: "1 !important",
    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",
    padding: "0 !important",
  },

  // Checkbox cell container styling (rows)
  "& .MuiDataGrid-cellCheckbox": {
    padding: "0 !important",
    minWidth: "52px !important",
    maxWidth: "52px !important",
    width: "52px !important",
    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",
  },

  // ============================================================================
  // UNIFIED CHECKBOX ICON STYLING - Apply same style to header AND rows
  // ============================================================================
  
  // Base checkbox styling (applies to both header and rows)
  "& .MuiCheckbox-root": {
    color: "#56656B !important",
    padding: "9px !important",
    visibility: "visible !important",
    opacity: "1 !important",
    display: "inline-flex !important",
    backgroundColor: "transparent !important",
    border: "none !important",
    borderRadius: "4px !important",
    width: "40px !important",
    height: "40px !important",
  },

  // Checkbox icon (SVG) styling
  "& .MuiCheckbox-root .MuiSvgIcon-root": {
    fontSize: "20px !important",
    width: "20px !important",
    height: "20px !important",
    backgroundColor: "transparent !important",
    visibility: "visible !important",
    opacity: "1 !important",
    fill: "currentColor !important",
  },

  // Checked state
  "& .MuiCheckbox-root.Mui-checked": {
    color: "#09779E !important",
  },

  "& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root": {
    color: "#09779E !important",
  },

  // Indeterminate state (minus icon for partial selection)
  "& .MuiCheckbox-root.MuiCheckbox-indeterminate": {
    color: "#09779E !important",
  },

  "& .MuiCheckbox-root.MuiCheckbox-indeterminate .MuiSvgIcon-root": {
    color: "#09779E !important",
  },

  // Hover effect for all checkboxes
  "& .MuiCheckbox-root:hover": {
    backgroundColor: "rgba(9, 119, 158, 0.04) !important",
  },

  // Disabled state
  "& .MuiCheckbox-root.Mui-disabled": {
    color: "#B8C4C9 !important",
    opacity: "0.5 !important",
  },

  // ============================================================================
  // HEADER CHECKBOX SPECIFIC OVERRIDES - Ensure consistency with rows
  // ============================================================================
  
  "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
    color: "#56656B !important",
    padding: "9px !important",
    backgroundColor: "transparent !important",
    visibility: "visible !important",
    opacity: "1 !important",
    width: "40px !important",
    height: "40px !important",
  },

  "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root .MuiSvgIcon-root": {
    fontSize: "20px !important",
    width: "20px !important",
    height: "20px !important",
    backgroundColor: "transparent !important",
    visibility: "visible !important",
    opacity: "1 !important",
    color: "inherit !important",
  },

  "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.Mui-checked": {
    color: "#09779E !important",
  },

  "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.MuiCheckbox-indeterminate": {
    color: "#09779E !important",
  },

  "& .MuiDataGrid-columnHeaderCheckbox .MuiButtonBase-root": {
    backgroundColor: "transparent !important",
    visibility: "visible !important",
    opacity: "1 !important",
  },

  // ============================================================================
  // ROW CHECKBOX CELL BACKGROUNDS - Maintain row styling consistency
  // Lower specificity to allow selection styles to override
  // ============================================================================
  
  // Normal rows (non-selected)
  "& .MuiDataGrid-row:not(.Mui-selected) .MuiDataGrid-cellCheckbox": {
    backgroundColor: "#ffffff !important",
  },

  // Row hover state (non-selected)
  "& .MuiDataGrid-row:not(.Mui-selected):hover .MuiDataGrid-cellCheckbox": {
    backgroundColor: "#f8f9fa !important",
  },

  // Even rows (non-selected)
  "& .MuiDataGrid-row:not(.Mui-selected):nth-of-type(even) .MuiDataGrid-cellCheckbox": {
    backgroundColor: "#fdfdfd !important",
  },

  // Even rows hover (non-selected)
  "& .MuiDataGrid-row:not(.Mui-selected):nth-of-type(even):hover .MuiDataGrid-cellCheckbox": {
    backgroundColor: "#f8f9fa !important",
  },

  // Pinned left checkbox cells (non-selected)
  "& .MuiDataGrid-row:not(.Mui-selected) .MuiDataGrid-cell--pinnedLeft.MuiDataGrid-cellCheckbox": {
    backgroundColor: "#ffffff !important",
  },

  "& .MuiDataGrid-row:not(.Mui-selected):hover .MuiDataGrid-cell--pinnedLeft.MuiDataGrid-cellCheckbox": {
    backgroundColor: "#f8f9fa !important",
  },

  "& .MuiDataGrid-row:not(.Mui-selected):nth-of-type(even) .MuiDataGrid-cell--pinnedLeft.MuiDataGrid-cellCheckbox": {
    backgroundColor: "#fdfdfd !important",
  },

  "& .MuiDataGrid-row:not(.Mui-selected):nth-of-type(even):hover .MuiDataGrid-cell--pinnedLeft.MuiDataGrid-cellCheckbox": {
    backgroundColor: "#f8f9fa !important",
  },
}));

const Container = styled.div`
  position: relative;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 100%;
  height: ${({ $autoHeight }) => ($autoHeight ? "auto" : "calc(100vh - 190px)")}; /* Overridden by inline style when provided */
  overflow: visible;
  box-sizing: border-box;

  .MuiDataGrid-pinnedColumns {
    box-shadow: none !important;
  }

  .MuiDataGrid-pinnedColumnHeaders {
    box-shadow: none !important;
  }

   .MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-sortIcon {
    color: #666;
  }

  .MuiDataGrid-menuIcon > button > svg {
    color: #666;
  }

  .MuiDataGrid-columnHeader .MuiDataGrid-filterIcon {
    color: #666;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: ${({ $autoHeight }) => ($autoHeight ? "auto" : "100%")};
  overflow: visible;
  box-sizing: border-box;
`;

const StatusCell = styled.div`
  font-weight: 400;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;

  &.status-not-started {
    color: #6b7280;
  }

  &.status-on-track {
    background-color: #ebfff5;
    color: #046744;
  }

  &.status-completed {
    background-color: #ebfff5;
    color: #046744;
  }

  &.status-delayed {
    background-color: #fff7f2;
    color: #9c3901;
  }

  &.status-new {
    background-color: #edf3f4;
    color: #01151d;
  }

  &.status-cancelled {
    background-color: #fef2f2;
    color: #991b1b;
  }

  &.status-default {
    color: #6b7280;
  }
`;

const LinkStyle = styled.div`
  overflow: hidden;
  color: var(--text-interaction-default, #09779e);
  text-overflow: ellipsis;
  font-family: "Source Sans Pro";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.04px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
  cursor: pointer;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  flex: 1 0 0;

  &:hover {
    color: #075a7a;
  }
`;

export {
  Container,
  TableContainer,
  StatusCell,
  LinkStyle,
  StickyDataGrid,
};

